import { useMutation } from '@apollo/client';
import type { DeleteAssessmentResultsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DeleteAssessmentResultsDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteDocumentAssessmentResultsTRPC } from './useDeleteDocumentAssessmentResultsTRPC';
import { useDeleteObligationAssessmentResultsTRPC } from './useDeleteObligationAssessmentResultsTRPC';
import { useDeleteRiskAssessmentResultsTRPC } from './useDeleteRiskAssessmentResultsTRPC';
import { useDeleteTestResultsForAssessmentsTRPC } from './useDeleteTestResultsForAssessmentsTRPC';

type AssessmentResultEntityType =
  | 'risk_assessment_result'
  | 'document_assessment_result'
  | 'obligation_assessment_result'
  | 'test_result';

const ASSESSMENT_RESULT_ENTITY_TYPES = [
  'risk_assessment_result',
  'document_assessment_result',
  'obligation_assessment_result',
  'test_result',
] as const satisfies readonly AssessmentResultEntityType[];

const isAssessmentResultEntityType = (
  value: string
): value is AssessmentResultEntityType =>
  (ASSESSMENT_RESULT_ENTITY_TYPES as readonly string[]).includes(value);

/**
 * Groups ids by the per-type tRPC delete endpoint that owns them.
 *
 * COI-487: `items` is typed to the four supported result types, so an
 * unsupported one is normally a compile error. This throws on one reaching
 * here anyway — from untyped data, or a fifth branch added to a flattener —
 * because the previous behaviour was to skip it with a `console.warn` while
 * `mapTrpcResponseToGraphQL` still counted it as deleted, so the user saw a
 * success toast for records that were never touched. Internal audit and
 * second-line results live in separate tables with their own delete
 * endpoints; they are not routed here.
 */
const groupIdsByEntityType = (
  items: { Id: string; ResultType: AssessmentResultEntityType }[]
): Record<AssessmentResultEntityType, string[]> => {
  const grouped: Record<AssessmentResultEntityType, string[]> = {
    risk_assessment_result: [],
    document_assessment_result: [],
    obligation_assessment_result: [],
    test_result: [],
  };

  for (const item of items) {
    if (!isAssessmentResultEntityType(item.ResultType)) {
      throw new Error(
        `useDeleteAssessmentResults: no delete endpoint for result type "${item.ResultType}" — refusing to report a delete that did not happen`
      );
    }
    grouped[item.ResultType].push(item.Id);
  }

  return grouped;
};

/**
 * Maps tRPC response back to GraphQL mutation shape for compatibility
 */
const mapTrpcResponseToGraphQL = (
  numIds: number
): DeleteAssessmentResultsMutation => ({
  delete_document_assessment_result: {
    __typename: 'document_assessment_result_mutation_response',
    affected_rows: numIds,
  },
  delete_obligation_assessment_result: {
    __typename: 'obligation_assessment_result_mutation_response',
    affected_rows: numIds,
  },
  delete_risk_assessment_result: {
    __typename: 'risk_assessment_result_mutation_response',
    affected_rows: numIds,
  },
  delete_test_result: {
    __typename: 'test_result_mutation_response',
    affected_rows: numIds,
  },
  delete_relation_file: {
    __typename: 'relation_file_mutation_response',
    affected_rows: numIds,
  },
});

export const useDeleteAssessmentResults = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteAssessmentResultsGraphQL, graphqlState] = useMutation(
    DeleteAssessmentResultsDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_assessment_result');
        evictField(cache, 'document_assessment_result');
        evictField(cache, 'obligation_assessment_result');
        evictField(cache, 'test_result');
        evictField(cache, 'assessment');
        evictField(cache, 'risk_assessment_result_aggregate');
        evictField(cache, 'document_assessment_result_aggregate');
        evictField(cache, 'obligation_assessment_result_aggregate');
        evictField(cache, 'risk_score');
      },
      refetchQueries: [namedOperations.Query.getRiskScoresByRiskId],
    }
  );

  // Per-type tRPC mutations
  const riskMutation = useDeleteRiskAssessmentResultsTRPC();
  const documentMutation = useDeleteDocumentAssessmentResultsTRPC();
  const obligationMutation = useDeleteObligationAssessmentResultsTRPC();
  const testResultMutation = useDeleteTestResultsForAssessmentsTRPC();

  const deleteAssessmentResults = async (variables: {
    items: { Id: string; ResultType: AssessmentResultEntityType }[];
  }): Promise<DeleteAssessmentResultsMutation> => {
    if (trpcEnabled) {
      const grouped = groupIdsByEntityType(variables.items);
      const promises: Promise<void>[] = [];

      if (grouped.risk_assessment_result.length > 0) {
        promises.push(
          riskMutation.deleteRiskAssessmentResults(
            grouped.risk_assessment_result
          )
        );
      }
      if (grouped.document_assessment_result.length > 0) {
        promises.push(
          documentMutation.deleteDocumentAssessmentResults(
            grouped.document_assessment_result
          )
        );
      }
      if (grouped.obligation_assessment_result.length > 0) {
        promises.push(
          obligationMutation.deleteObligationAssessmentResults(
            grouped.obligation_assessment_result
          )
        );
      }
      if (grouped.test_result.length > 0) {
        promises.push(
          testResultMutation.deleteTestResults(grouped.test_result)
        );
      }

      await Promise.all(promises);

      return mapTrpcResponseToGraphQL(variables.items.length);
    }

    // GraphQL fallback: extract all IDs and call the single mutation
    const result = await deleteAssessmentResultsGraphQL({
      variables: {
        Ids: variables.items.map((i) => i.Id),
      },
    });

    if (!result.data) {
      throw new Error('Failed to delete assessment results');
    }

    return result.data;
  };

  const trpcLoading =
    riskMutation.isLoading ||
    documentMutation.isLoading ||
    obligationMutation.isLoading ||
    testResultMutation.isLoading;

  return {
    deleteAssessmentResults,
    deleteResult: {
      loading: trpcEnabled ? trpcLoading : graphqlState.loading,
    },
  };
};
